import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { reaction, runInAction } from 'mobx';
import { observer, useLocalStore } from 'mobx-react';
import React, { useEffect, useReducer, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ERROR_CODES, WLIDS } from 'Constants';
import CopySvg from 'images/copy.svg';
import { appConfigStore } from 'Models/AppConfigStore';
import { UserEntity } from 'Models/Entities';
import { IUserResult, store } from 'Models/Store';
import { whiteLabelStore } from 'Models/WhiteLabelStore';
import { useNavigate } from 'react-router-dom';
import {
	convertGuest,
	registerForCustomer,
	registerTeamMember,
	registerUser,
} from 'Services/Api/_HumanWritten/RegisterService';
import { checkUserEmail, resetPasswordRequest } from 'Services/Api/AccountService';
import {
	check2faStatus,
	guestLogin,
	EmailCodeRequired,
	isEmailCodeRequired,
	login,
	validate2faCode,
} from 'Services/Api/AuthorizationService';
import { getErrorMessages } from 'Util/GraphQLUtils';
import { checkIfInvalidName, upperCaseFirst } from 'Util/StringUtils';
import alertToast from 'Util/ToastifyUtils';
import { trackForgotPassword, trackLogin, trackSignUp } from 'Util/_HumanWritten/Analytics/analyticsEvents';
import { isEmail } from 'Validators/Functions/Email';
import { IsValidPhone } from 'Validators/Functions/HumanWritten/Phone';
import { ScrollToError } from 'Validators/Functions/HumanWritten/ScrollToError';
import Card from 'Views/Components/_HumanWritten/Card/Card';
import Errors from 'Views/Components/_HumanWritten/Errors/Errors';
import LinkAsButton from 'Views/Components/_HumanWritten/LinkAsButton/LinkAsButton';
import PinCodeInput from 'Views/Components/_HumanWritten/PinCodeInput/PinCodeInput';
import StyledQrCode from 'Views/Components/_HumanWritten/StyleQrCode';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import { Checkbox } from 'Views/Components/Checkbox/Checkbox';
import { Flex } from 'Views/Components/Flex/Flex';
import Password from 'Views/Components/Password/Password';
import LoaderInsideButton from 'Views/Components/Spinner/LoaderInsideButton';
import { Text } from 'Views/Components/Text/Text';
import { TextField } from 'Views/Components/TextBox/TextBox';
import { TextInput } from 'Views/Components/New/TextInput';
import { PasswordInput } from 'Views/Components/New/PasswordInput';
import { PhoneInputField } from 'Views/Components/New/PhoneInputField';
import { SelectInput } from 'Views/Components/New/SelectInput';
import { checkUserDetailsAndShowPopup } from 'Views/Pages/Login/LoginUtils';
import {
	EmailCodePurpose,
	initialLoginFlowState,
	LoginFlowActionCreators,
	LoginFlowMode,
	loginFlowReducer,
} from './loginFlowReducer';
import { clearFerryBookingTransactionIdFromStorage } from 'Services/Api/_HumanWritten/BookingService/BookingService';
import { clearBookingWizardData } from '../../BookingWizardDataStore';
import { clearBookingPricingCache } from '../../BookingWizardPricingCacheStore';

const AuthenticatorTokenType = 'Authenticator';
const GoogleAuthenticatorLink = 'https://support.google.com/accounts/answer/1066447';
const PinCodeLength = 6;

export default observer(LoginFlow);

interface LoginState {
	email: string;
	password: string;
	rememberMe: boolean;
	firstName: string;
	lastName: string;
	initials: string;
	errors: {
		email?: string;
		password?: string;
		[attr: string]: string | undefined;
	};
}

interface RegisterState {
	newUser: UserEntity;
	errors: Record<string, string | undefined>;
	emailSending: boolean;
	/**
	 * Whether the registrant ticked "Register as a company". Only offered while the
	 * User.companyName.enabled config is on, and never for team-member creates.
	 */
	registerAsCompany: boolean;
}

interface TwoFactorState {
	pinCode?: string;
	attemptsLeft?: number;
	rememberMe?: boolean;
}

interface GuestState {
	user: UserEntity;
	errors: Record<string, string | undefined>;
}

interface Initial {
	firstName?: string;
	lastName?: string;
	email?: string;
	phone?: string;
	postcode?: string;
}

interface LoginFlowProps {
	mode?: LoginFlowMode;
	initialParams?: Initial;
	afterLoginSuccess?: () => void;
	getWizardDataToken?: () => Promise<string | null>;
	formId?: string;
	registrationMessage?: React.ReactNode;
	onRegistrationComplete?: () => void;
	onModeChange?: (mode: string) => void;
	resetRef?: React.MutableRefObject<(() => void) | null>;
	voucherCode?: string;
	/**
	 * Fires whenever the in-flight login/register/etc. request transitions between
	 * loading states. Lets a parent modal disable its own Continue button (which
	 * lives in the modal footer outside this form) while a submission is in flight.
	 */
	onLoadingChange?: (loading: boolean) => void;
}

function LoginFlow({
	mode = 'default',
	initialParams,
	afterLoginSuccess,
	getWizardDataToken,
	formId,
	registrationMessage,
	onRegistrationComplete,
	onModeChange,
	resetRef,
	voucherCode,
	onLoadingChange,
}: LoginFlowProps) {
	const navigate = useNavigate();
	const intl = useIntl();
	const [state, dispatch] = useReducer(loginFlowReducer, initialLoginFlowState({ mode }));

	// Expose reset to parent and notify mode changes
	useEffect(() => {
		onModeChange?.(state.mode);
	}, [state.mode, onModeChange]);

	useEffect(() => {
		if (resetRef) {
			resetRef.current = () => dispatch(LoginFlowActionCreators.reset());
		}
	}, [resetRef]);

	const loading = useLocalStore<{ value: boolean }>(() => ({ value: false }));

	// Bridge the mobx loading observable out to the parent so it can mirror the
	// state on a Continue button rendered outside the form (e.g. modal footer).
	useEffect(() => {
		if (!onLoadingChange) return undefined;
		return reaction(
			() => loading.value,
			val => onLoadingChange(val),
			{ fireImmediately: true },
		);
		// `loading` is a stable mobx local store across renders — safe to omit from deps.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [onLoadingChange]);

	const localState = useLocalStore<LoginState>(() => ({
		email: '',
		password: '',
		firstName: '',
		lastName: '',
		initials: '',
		rememberMe: false,
		errors: {},
	}));

	const registerState = useLocalStore<RegisterState>(() => ({
		newUser: new UserEntity(initialParams),
		errors: {},
		emailSending: false,
		registerAsCompany: false,
	}));

	// Staff+ registering on behalf of someone else can pick a target role.
	// A user can only create another user with strictly lower privilege than
	// their own; the backend enforces the same rule via /api/register/team-member.
	const availableRoles = React.useMemo<Array<'User' | 'Staff' | 'Manager' | 'Admin'>>(() => {
		const roles: Array<'User' | 'Staff' | 'Manager' | 'Admin'> = ['User'];
		if (store.isManager) roles.push('Staff');
		if (store.isAdmin) roles.push('Manager');
		if (store.isSuper) roles.push('Admin');
		return roles;
	}, [store.isManager, store.isAdmin, store.isSuper]);
	const showRoleSelect = availableRoles.length > 1;
	const [selectedRole, setSelectedRole] = React.useState<'User' | 'Staff' | 'Manager' | 'Admin'>('User');
	const isTeamMember = selectedRole !== 'User';

	// The company option only applies to customer accounts — a team member is staff, not a
	// business. Deriving it here (rather than reading the flag directly) means a tick left behind
	// by a role switch, or by the config being turned off, can't leak into validation or the payload.
	const isCompanyRegistration =
		appConfigStore.userCompanyNameEnabled && !isTeamMember && registerState.registerAsCompany;

	// True whenever a staff+ user is creating someone else's account on this form.
	// Both customer creates (selectedRole === 'User') and team-member creates rely
	// on the server to generate a temporary password and email an activation link;
	// the new user sets their own password via that link. The form therefore hides
	// the password field and skips password validation in this mode. Public self-
	// registration (anonymous /register) keeps the password field as before.
	const isStaffRegisteringOnBehalf = store.isStaff;
	const passwordlessRegistration = isTeamMember || isStaffRegisteringOnBehalf;

	const passwordRef = useRef<HTMLInputElement>(null);

	const twoFactorState = useLocalStore<TwoFactorState>(() => ({}));

	const queryClient = useQueryClient();

	// The code emailed to a returning guest, before they can continue as that guest or turn it into an account.
	const emailCodeState = useLocalStore<{ pinCode?: string; attemptsLeft?: number }>(() => ({}));

	const guestState = useLocalStore<GuestState>(() => ({
		user: new UserEntity(),
		errors: {},
	}));

	useEffect(() => {
		switch (state.mode) {
			case 'initial': {
				runInAction(() => {
					localState.password = '';
				});
				break;
			}
			case 'emailConfirmed': {
				passwordRef.current?.focus();
				break;
			}
			case 'register': {
				if (localState.email) {
					runInAction(() => {
						registerState.newUser.email = localState.email;
					});
				}
				break;
			}
			case 'guestSetup': {
				if (localState.email) {
					runInAction(() => {
						guestState.user.email = localState.email;
					});
				}
				break;
			}
		}
	}, [state.mode]);

	const onEmailInvalid = (e: React.InvalidEvent<HTMLInputElement>) => {
		if (e.target.validity.valueMissing) {
			e.target.setCustomValidity(intl.formatMessage(WLIDS.validation.emailRequired));
		} else if (e.target.validity.typeMismatch && !e.target.value.includes('@')) {
			e.target.setCustomValidity("Please include an '@' in the email address.");
		} else if (e.target.validity.typeMismatch && (e.target.value.split('@')[1] || '').match(/[\s]/)) {
			e.target.setCustomValidity('Please enter a valid email address (no spaces allowed).');
		} else if (e.target.validity.typeMismatch && (e.target.value.split('@')[1] || '').match(/[:]/)) {
			e.target.setCustomValidity("A part following '@' the symbol ':'.");
		} else if (e.target.validity.typeMismatch || e.target.value === '') {
			e.target.setCustomValidity('Please enter a valid email address.');
		} else {
			e.target.setCustomValidity('Invalid email format.');
		}
	};

	// Check if the email entered already has an account, if not redirect them to the registration page
	const onContinueClicked = async () => {
		runInAction(async () => {
			localState.errors = {};

			if (!localState.email) {
				localState.errors.email = intl.formatMessage(WLIDS.validation.emailRequired);
			} else if (!isEmail(localState.email)) {
				localState.errors.email = 'Invalid email address';
			}
			if (localState.errors.email === undefined) {
				loading.value = true;

				try {
					const result = await checkUserEmail(localState.email);
					if (result?.isDisabled ?? false) {
						runInAction(() => {
							localState.errors.email = 'Account is disabled. Contact staff for assistance.';
						});
					}
					if (result && !result.isDisabled) {
						if (result.resetRequired) {
							// migratedUser picks the wording on the other side: customers carried over
							// from the legacy system are told their bookings came across with them,
							// customers whose account staff created are simply asked to activate it.
							const migrated = result.isMigratedUser ? '&migratedUser=true' : '';
							navigate(`/reset-password-request?email=${localState.email}&forcedReset=true${migrated}`);
						} else if (result.isGuest) {
							dispatch(LoginFlowActionCreators.convertGuest());
							runInAction(() => {
								registerState.newUser.email = localState.email;
								registerState.newUser.firstName = result.firstName ?? '';
							});
						} else {
							dispatch(LoginFlowActionCreators.confirmAccountExists(result));
						}
					} else if (!result?.isDisabled) {
						dispatch(LoginFlowActionCreators.register());
					}
				} catch (err) {
					console.error(err);
					alertToast('Something went wrong checking the email', 'error');
				} finally {
					runInAction(() => {
						loading.value = false;
					});
				}
			}
		});
	};

	const onLoginClicked = () => {
		runInAction(() => {
			if (!localState.password) {
				localState.errors.password = 'Password is required';
			}
			if (Object.keys(localState.errors).length === 0) {
				runInAction(() => {
					loading.value = true;
				});

				login(localState.email, localState.password, localState.rememberMe)
					.then(async data => {
						if (data.type === '2fa-required') {
							try {
								const res = await check2faStatus();
								if (res.twoFactorSetupRequired) {
									dispatch(LoginFlowActionCreators.twoFactorRequired(res));
								} else {
									dispatch(LoginFlowActionCreators.twoFactorRequired());
								}
							} catch (err) {
								if (isAxiosError(err)) {
									if (err.response?.status === 403) {
										dispatch(
											LoginFlowActionCreators.accountLocked(err.response.data?.lockoutMinutes),
										);
										return;
									}
								}

								alertToast('Session is expired', 'error');
							}
						} else if (data.type === 'invalid-password') {
							alertToast('Your current password is invalid, please reset it.', 'error');
						} else {
							onLoginSuccess(data);
						}

						runInAction(() => {
							loading.value = false;
						});
					})
					.catch(response => {
						const errorMessages = <p>{response?.response?.data}</p>;
						alertToast(errorMessages, 'error', 'Login failed');

						runInAction(() => {
							loading.value = false;
						});
					});
			}
		});
	};

	const onLoginSuccess = (userResult: IUserResult) => {
		store.setLoggedInUser(userResult);
		trackLogin();

		checkUserDetailsAndShowPopup(navigate);

		// We don't want intercom chat bubble in the right bottom corner to appear for staff+ users
		if (store.isStaff) {
			document.getElementsByClassName('intercom-lightweight-app')[0]?.remove();
		}

		afterLoginSuccess?.();
	};

	const onSubmitRegisterClicked = async () => {
		runInAction(() => {
			registerState.errors = {};
		});
		if (registerState.newUser) {
			validate(registerState.newUser);
			if (Object.keys(registerState.errors).length > 0) {
				ScrollToError('end');
				return;
			}
			submitRegister();
		}
	};

	const submitRegister = async () => {
		runInAction(() => {
			registerState.emailSending = true;
			loading.value = true;
		});

		const wizardDataToken = getWizardDataToken === undefined ? null : await getWizardDataToken();

		try {
			if (isTeamMember) {
				await registerTeamMember({
					email: registerState.newUser.email,
					role: selectedRole as 'Staff' | 'Manager' | 'Admin',
					firstName: registerState.newUser.firstName,
					lastName: registerState.newUser.lastName,
					phone: registerState.newUser.phone || undefined,
					postcode: registerState.newUser.postcode || undefined,
				});
			} else if (isStaffRegisteringOnBehalf) {
				// Staff registering a customer: server generates a temp password
				// and sends an activate-account email. The customer sets their
				// own password via that link.
				await registerForCustomer(registerState.newUser);
			} else {
				await registerUser('user-entity', {
					registrationModel: registerState.newUser.toJSON({ password: {} }),
					wizardDataToken,
					voucherCode: voucherCode || undefined,
				});
			}

			if (isTeamMember || isStaffRegisteringOnBehalf) {
				// The Users table (AccessPermissionsList) caches its pages under 'fetchUsers'; without this,
				// "Back to users" shows the list from before the account existed.
				queryClient.invalidateQueries({ queryKey: ['fetchUsers'] });
			}

			dispatch(LoginFlowActionCreators.sentRegistration());
			trackSignUp();
			onRegistrationComplete?.();
		} catch (response: any) {
			if (response?.response?.status === 403) {
				alertToast('You are not allowed to create a user with that role.', 'error');
				return;
			}
			if (response?.response?.data.code === ERROR_CODES.PHONE_ALREADY_EXISTS) {
				runInAction(() => {
					registerState.errors.phone = intl.formatMessage(WLIDS.validation.phoneAlreadyExists);
				});
				return;
			}

			const passwordErrorCodeToWlid: Record<string, any> = {
				[ERROR_CODES.PASSWORD_TOO_SHORT]: WLIDS.validation.passwordTooShort,
				[ERROR_CODES.PASSWORD_REQUIRES_NON_ALPHANUMERIC]: WLIDS.validation.passwordRequiresNonAlphanumeric,
				[ERROR_CODES.PASSWORD_REQUIRES_DIGIT]: WLIDS.validation.passwordRequiresDigit,
				[ERROR_CODES.PASSWORD_REQUIRES_LOWER]: WLIDS.validation.passwordRequiresLower,
				[ERROR_CODES.PASSWORD_REQUIRES_UPPER]: WLIDS.validation.passwordRequiresUpper,
				[ERROR_CODES.PASSWORD_REQUIRES_UNIQUE_CHARS]: WLIDS.validation.passwordRequiresUniqueChars,
			};

			const wlid = passwordErrorCodeToWlid[response?.response?.data.code];
			if (wlid) {
				runInAction(() => {
					registerState.errors.password = intl.formatMessage(wlid, {
						length: store.passwordConfiguration.requiredLength,
						count: store.passwordConfiguration.requiredUniqueChars,
					});
				});
				return;
			}
			const errorMessages = getErrorMessages(response).map((error: any) =>
				typeof error === 'string' ? error : (error?.message ?? ''),
			);
			const firstError = errorMessages[0] ?? '';
			let modifiedErrorMessage: string | null = null;
			if (appConfigStore.userPhoneEnabled) {
				if (firstError.includes('Phone') || firstError.includes('Username') || firstError.includes('Email')) {
					modifiedErrorMessage = 'An account with this phone number or email already exists';
				}
			} else if (firstError.includes('Username') || firstError.includes('Email')) {
				modifiedErrorMessage = 'An account with this email already exists';
			}
			alertToast(modifiedErrorMessage ?? errorMessages, 'error', 'Registration was not complete');
		} finally {
			runInAction(() => {
				registerState.emailSending = false;
				loading.value = false;
			});
		}
	};

	const validate = (model: UserEntity) => {
		// Passwordless flows (team member / customer registered on behalf) have no password field —
		// the server generates one and emails an activation link.
		const errors = model.validateRegistration({
			requirePassword: !passwordlessRegistration,
			isTeamMember: isTeamMember,
			isCompany: isCompanyRegistration,
		});
		runInAction(() => {
			registerState.errors = errors;
		});
	};

	const onResetPasswordClicked = () => {
		runInAction(async () => {
			localState.errors = {};
			if (!localState.email) {
				localState.errors.email = intl.formatMessage(WLIDS.validation.required);
			} else if (!isEmail(localState.email)) {
				localState.errors.email = 'Invalid email address';
			}
			if (localState.errors.email === undefined) {
				loading.value = true;
				resetPasswordRequest(localState.email)
					.then(() => {
						dispatch(LoginFlowActionCreators.forgotPassword(true));
						trackForgotPassword();
					})
					.catch(response => {
						const errorMessages = getErrorMessages(response).map((error: any) => <p>{error.message}</p>);
						alertToast(
							<div>
								<h6>
									<FormattedMessage {...WLIDS.auth.couldNotSendRequest} />
								</h6>
								{errorMessages}
							</div>,
							'error',
						);
					})
					.finally(() => {
						runInAction(() => {
							loading.value = false;
						});
					});
			}
		});
	};

	const formatCode = (code: string): string => {
		let result = '';
		for (let i = 0; i < code.length; i += 4) {
			// Adding a trailing space is less confusing with concat compared to template strings
			// eslint-disable-next-line prefer-template
			result += code.substring(i, 4) + ' ';
		}
		return result.trim();
	};

	const onTwoFactorSubmit = async () => {
		if (!twoFactorState.pinCode || twoFactorState.pinCode?.length !== PinCodeLength) {
			return;
		}

		try {
			runInAction(() => {
				loading.value = true;
			});
			const res = await validate2faCode({
				token: twoFactorState.pinCode,
				method: AuthenticatorTokenType,
				rememberTwoFactor: twoFactorState.rememberMe,
				rememberMe: localState.rememberMe,
			});

			store.setLoggedInUser(res.data);
			afterLoginSuccess?.();
		} catch (err) {
			if (!isAxiosError(err)) {
				alertToast('Something went wrong with 2FA', 'error');
				return;
			}

			if (err.response?.status === 403) {
				dispatch(LoginFlowActionCreators.accountLocked(err.response.data?.lockoutMinutes));
			} else if (err.response?.status === 401) {
				runInAction(() => {
					twoFactorState.attemptsLeft = (err as any).response?.data.attemptsLeft;
					twoFactorState.pinCode = undefined;
				});
				alertToast('Authentication code is invalid.', 'error');
			} else {
				alertToast('Session is expired', 'error');
				dispatch(LoginFlowActionCreators.reset());
			}
		} finally {
			runInAction(() => {
				loading.value = false;
			});
		}
	};

	/**
	 * The 401s a wrong or exhausted emailed code produces. Returns true when the error was one of them.
	 */
	const handleEmailCodeError = (error: any): boolean => {
		const code = error?.response?.data?.code;
		if (code === ERROR_CODES.EMAIL_CODE_LOCKED) {
			dispatch(LoginFlowActionCreators.accountLocked(error.response.data?.lockoutMinutes));
			return true;
		}
		if (code === ERROR_CODES.EMAIL_CODE_INVALID) {
			runInAction(() => {
				emailCodeState.attemptsLeft = error.response.data.attemptsLeft;
				emailCodeState.pinCode = undefined;
			});
			return true;
		}
		return false;
	};

	/**
	 * The server answered 202: a code is needed. Show the code step, or on a resend say whether a new code
	 * actually went out - one sent moments ago is not replaced, and saying "sent" then was misleading.
	 */
	const onEmailCodeRequested = (purpose: EmailCodePurpose, resend: boolean, response: EmailCodeRequired) => {
		if (resend && !response.sent) {
			alertToast(
				intl.formatMessage(WLIDS.auth.emailCodeWait, { seconds: response.resendAvailableInSeconds }),
				'info',
			);
			return;
		}
		runInAction(() => {
			emailCodeState.pinCode = undefined;
			emailCodeState.attemptsLeft = undefined;
		});
		if (resend) {
			alertToast(intl.formatMessage(WLIDS.auth.emailCodeResent), 'success');
		} else {
			dispatch(LoginFlowActionCreators.emailCodeRequired(purpose));
		}
	};

	/**
	 * Continue as a guest. For an email that already belongs to a guest the server emails a code instead of
	 * signing in, and this runs again from the code step with `code` set.
	 */
	const onContinueAsGuest = (code?: string, resend = false) => {
		runInAction(() => {
			guestState.errors = {};

			const { email, firstName, lastName, phone, postcode } = guestState.user;

			if (!email) {
				guestState.errors.email = intl.formatMessage(WLIDS.validation.emailRequired);
			} else if (!isEmail(email)) {
				guestState.errors.email = 'Invalid email address';
			}

			if (appConfigStore.userPhoneEnabled && !IsValidPhone(phone, !appConfigStore.userPhoneRequired)) {
				guestState.errors.phone = 'Invalid phone number';
			}

			if (!firstName) {
				guestState.errors.firstName = intl.formatMessage(WLIDS.validation.required);
			} else if (checkIfInvalidName(firstName)) {
				guestState.errors.firstName = intl.formatMessage(WLIDS.validation.nameInvalid);
			}

			if (!lastName) {
				guestState.errors.lastName = intl.formatMessage(WLIDS.validation.required);
			} else if (checkIfInvalidName(lastName)) {
				guestState.errors.lastName = intl.formatMessage(WLIDS.validation.nameInvalid);
			}

			if (appConfigStore.userPostcodeEnabled && !postcode) {
				guestState.errors.postcode = intl.formatMessage(WLIDS.validation.required);
			}

			if (Object.keys(guestState.errors).length === 0) {
				runInAction(() => {
					loading.value = true;
				});
				guestLogin({ email, firstName, lastName, phone, postcode, code })
					.then(async data => {
						if (isEmailCodeRequired(data)) {
							onEmailCodeRequested('guestLogin', resend, data);
							return;
						}
						onLoginSuccess(data);
					})
					.catch(response => {
						if (handleEmailCodeError(response)) {
							return;
						}
						if (response?.response?.data?.code === ERROR_CODES.EMAIL_ALREADY_EXISTS) {
							runInAction(() => {
								guestState.errors.email = intl.formatMessage(WLIDS.validation.emailAlreadyExists);
							});
						} else {
							alertToast('', 'error', 'Login failed');
						}
					})
					.finally(() => {
						runInAction(() => {
							loading.value = false;
						});
					});
			}
		});
	};

	/**
	 * Turn a guest into an account. Like continuing as a guest, the first attempt emails a code and this runs
	 * again from the code step with `code` set.
	 */
	const onConvertGuest = async (code?: string, resend = false) => {
		runInAction(() => {
			registerState.errors = {};
			loading.value = true;
		});
		if (registerState.newUser) {
			validate(registerState.newUser);
			if (Object.keys(registerState.errors).length > 0) {
				ScrollToError('end');
				runInAction(() => {
					loading.value = false;
				});
				return;
			}
			runInAction(() => {
				registerState.emailSending = true;
			});

			convertGuest(registerState.newUser.toJSON({ password: {} }), code)
				.then(response => {
					if (isEmailCodeRequired(response.data)) {
						runInAction(() => {
							registerState.emailSending = false;
						});
						onEmailCodeRequested('convertGuest', resend, response.data);
						return;
					}
					dispatch(LoginFlowActionCreators.sentRegistration());
					onRegistrationComplete?.();

					// Logout
					clearBookingWizardData();
					clearBookingPricingCache();
					clearFerryBookingTransactionIdFromStorage();
					store.clearLoggedInUser();
				})
				.catch(response => {
					runInAction(() => {
						registerState.emailSending = false;
					});
					if (handleEmailCodeError(response)) {
						return;
					}
					if (response.response?.data.message === 'User is not a guest.') {
						alertToast(
							'This email address is already associated with an account. Please sign in.',
							'error',
							'Account already exists',
						);
						return;
					}
					if (response?.response?.data.code === ERROR_CODES.PHONE_ALREADY_EXISTS) {
						runInAction(() => {
							registerState.errors.phone = intl.formatMessage(WLIDS.validation.phoneAlreadyExists);
						});
						return;
					}

					const passwordErrorCodeToWlid: Record<string, any> = {
						[ERROR_CODES.PASSWORD_TOO_SHORT]: WLIDS.validation.passwordTooShort,
						[ERROR_CODES.PASSWORD_REQUIRES_NON_ALPHANUMERIC]:
							WLIDS.validation.passwordRequiresNonAlphanumeric,
						[ERROR_CODES.PASSWORD_REQUIRES_DIGIT]: WLIDS.validation.passwordRequiresDigit,
						[ERROR_CODES.PASSWORD_REQUIRES_LOWER]: WLIDS.validation.passwordRequiresLower,
						[ERROR_CODES.PASSWORD_REQUIRES_UPPER]: WLIDS.validation.passwordRequiresUpper,
						[ERROR_CODES.PASSWORD_REQUIRES_UNIQUE_CHARS]: WLIDS.validation.passwordRequiresUniqueChars,
					};
					const wlid = passwordErrorCodeToWlid[response?.response?.data.code];
					if (wlid) {
						runInAction(() => {
							registerState.errors.password = intl.formatMessage(wlid, {
								length: store.passwordConfiguration.requiredLength,
								count: store.passwordConfiguration.requiredUniqueChars,
							});
						});
						return;
					}

					const errorMessages = getErrorMessages(response).map((error: any) => error.message);
					let modifiedErrorMessage: string | null = null;
					if (appConfigStore.userPhoneEnabled) {
						if (errorMessages[0].includes('Phone') || errorMessages[0].includes('Username')) {
							modifiedErrorMessage = 'An account with this phone number or email already exists';
						}
					} else if (errorMessages[0].includes('Username')) {
						modifiedErrorMessage = 'An account with this email already exists';
					}
					alertToast(modifiedErrorMessage ?? errorMessages, 'error', 'Registration was not complete');
				})
				.finally(() => {
					runInAction(() => {
						loading.value = false;
					});
				});
		}
	};

	const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		// Guard against double-submits: each handler below sets loading.value=true and
		// awaits an API call before resetting. If a second submit lands while one is
		// in flight, the API would be hit twice and the second call usually returns an
		// error that makes it look like the first one failed.
		if (loading.value) {
			return undefined;
		}

		if (state.mode === 'initial') {
			return onContinueClicked();
		}

		if (state.mode === 'emailConfirmed') {
			return onLoginClicked();
		}

		if (state.mode === 'register') {
			return onSubmitRegisterClicked();
		}

		if (state.mode === 'forgotPassword') {
			return onResetPasswordClicked();
		}

		if (state.mode === 'twoFactorRequired') {
			return onTwoFactorSubmit();
		}

		if (state.mode === 'guestSetup') {
			return onContinueAsGuest();
		}

		if (state.mode === 'convertGuest') {
			return onConvertGuest();
		}

		if (state.mode === 'emailCode') {
			if (emailCodeState.pinCode?.length !== PinCodeLength) {
				return undefined;
			}
			return state.purpose === 'guestLogin'
				? onContinueAsGuest(emailCodeState.pinCode)
				: onConvertGuest(emailCodeState.pinCode);
		}

		return () => {};
	};

	return (
		<div className="login-flow">
			<form onSubmit={onSubmit} id={formId}>
				<Card>
					<h5>
						{state.mode === 'initial' && <FormattedMessage {...WLIDS.auth.loginOrSignUp} />}
						{state.mode === 'emailConfirmed' && (
							<>
								{state.firstName
									? `${intl.formatMessage(WLIDS.auth.welcomeBack)}, ${upperCaseFirst(state.firstName.toLowerCase())}`
									: intl.formatMessage(WLIDS.auth.welcomeBack)}
							</>
						)}
						{state.mode === 'register' && <FormattedMessage {...WLIDS.auth.registerNewUser} />}
						{state.mode === 'sentRegistration' &&
							(store.isStaff ? (
								'Activation email sent'
							) : (
								<FormattedMessage {...WLIDS.auth.checkYourInbox} />
							))}
						{state.mode === 'forgotPassword' && <FormattedMessage {...WLIDS.auth.forgotPassword} />}
						{state.mode === 'twoFactorRequired' && (
							<>
								{state.setupRequired && <FormattedMessage {...WLIDS.auth.setupTwoFactorAuth} />}
								{!state.setupRequired && <FormattedMessage {...WLIDS.auth.verifyYourIdentity} />}
							</>
						)}
						{state.mode === 'accountLocked' && <FormattedMessage {...WLIDS.auth.accountLocked} />}
						{state.mode === 'guestSetup' && <FormattedMessage {...WLIDS.guest.details} />}
						{state.mode === 'convertGuest' && <FormattedMessage {...WLIDS.actions.createAccount} />}
						{state.mode === 'emailCode' && <FormattedMessage {...WLIDS.auth.verifyYourIdentity} />}
					</h5>
					<Flex direction="col" gap="md">
						{state.mode === 'initial' && (
							<>
								<TextField
									model={localState}
									modelProperty="email"
									label={<FormattedMessage {...WLIDS.form.email} />}
									placeholder={intl.formatMessage(WLIDS.form.emailPlaceholder)}
									inputProps={
										{
											className: '!m-0',
											autoComplete: 'username',
											type: 'email',
											onInvalid: onEmailInvalid,
											onInput: (e: React.FormEvent<HTMLInputElement>) =>
												e.currentTarget.setCustomValidity(''),
											'data-test-id': 'wizard-login-email-input',
										} as any
									}
									isRequired
									autoFocus
									onAfterChange={() => {
										runInAction(() => {
											localState.errors.email = undefined;
										});
									}}
									errors={localState.errors.email}
								/>
								<Flex direction="col" gap="xs">
									<Button type="submit" display={Display.Solid} colors={Colors.Primary} newStyles>
										<FormattedMessage {...WLIDS.actions.continue} />
										{loading.value && <LoaderInsideButton />}
									</Button>
									{appConfigStore.guestBookingsEnabled && (
										<Button
											display={Display.Solid}
											colors={Colors.Secondary}
											newStyles
											onClick={() => dispatch(LoginFlowActionCreators.guestSetup())}
											buttonProps={{ 'data-test-id': 'wizard-continue-as-guest-button' } as any}
										>
											<FormattedMessage {...WLIDS.actions.continueAsGuest} />
										</Button>
									)}
								</Flex>
							</>
						)}
						{state.mode === 'emailConfirmed' && (
							<>
								<Flex direction="col" gap="sm">
									<Flex direction="col" gap="xxs">
										<Password
											inputRef={passwordRef}
											model={localState}
											modelProperty="password"
											label={<FormattedMessage {...WLIDS.form.password} />}
											placeholder={intl.formatMessage(WLIDS.form.password)}
											inputProps={{
												autoComplete: 'current-password',
												style: {
													visibility: state.mode === 'emailConfirmed' ? 'visible' : 'hidden',
												},
											}}
											isRequired
											// A failed-attempt lockout sits under the field, the way the email
											// step shows "Account is disabled" under the email.
											errors={
												localState.errors.password ??
												(state.lockoutMinutes
													? intl.formatMessage(WLIDS.auth.signInLockedOut, {
															minutes: state.lockoutMinutes,
														})
													: undefined)
											}
										/>
										<Button
											display={Display.Text}
											className="forgotten-password"
											onClick={() => dispatch(LoginFlowActionCreators.forgotPassword())}
										>
											<FormattedMessage {...WLIDS.auth.forgotPassword} />
										</Button>
									</Flex>
									<Checkbox
										model={localState}
										modelProperty="rememberMe"
										label={intl.formatMessage(WLIDS.auth.stayLoggedIn)}
										onChecked={(_, checked) => {
											runInAction(() => {
												localState.rememberMe = checked;
											});
										}}
									/>
								</Flex>
								<Flex direction="col" gap="xs">
									<Flex gap="xs">
										<Button
											display={Display.Outline}
											colors={Colors.Secondary}
											newStyles
											onClick={() => dispatch(LoginFlowActionCreators.reset())}
											buttonProps={{ style: { flex: 2 } }}
										>
											<FormattedMessage {...WLIDS.actions.back} />
										</Button>
										<Button
											type="submit"
											display={Display.Solid}
											colors={Colors.Primary}
											newStyles
											buttonProps={{ style: { flex: 5 } }}
										>
											<FormattedMessage {...WLIDS.auth.login} />
											{loading.value && <LoaderInsideButton />}
										</Button>
									</Flex>
								</Flex>
							</>
						)}
						{(state.mode === 'register' || state.mode === 'convertGuest') && (
							<>
								<Flex direction="col" gap="xs">
									{showRoleSelect && state.mode === 'register' && (
										<SelectInput
											className="login-flow__role-select"
											label="Role"
											required
											options={availableRoles.map(r => ({
												value: r,
												label: r === 'User' ? 'Customer' : r,
											}))}
											value={{
												value: selectedRole,
												label: selectedRole === 'User' ? 'Customer' : selectedRole,
											}}
											onChange={(option: any) =>
												setSelectedRole((option?.value ?? 'User') as typeof selectedRole)
											}
											isSearchable={false}
											isClearable={false}
										/>
									)}
									{appConfigStore.userCompanyNameEnabled && !isTeamMember && (
										<Checkbox
											model={registerState}
											modelProperty="registerAsCompany"
											label={intl.formatMessage(WLIDS.form.registerAsCompany)}
											inputProps={{ 'data-test-id': 'register-as-company-checkbox' } as any}
											onChecked={(_, checked) => {
												runInAction(() => {
													registerState.registerAsCompany = checked;
													if (!checked) {
														// Drop the value so an abandoned company name is
														// never submitted, and clear its stale error.
														registerState.newUser.companyName = undefined;
														registerState.errors.companyName = undefined;
													}
												});
											}}
										/>
									)}
									{isCompanyRegistration && (
										<TextInput
											label={intl.formatMessage(WLIDS.form.companyName)}
											placeholder={intl.formatMessage(WLIDS.form.companyNamePlaceholder)}
											required
											data-test-id="register-company-name-input"
											value={registerState.newUser.companyName ?? ''}
											onChange={e =>
												runInAction(() => {
													registerState.newUser.companyName = e.target.value;
													registerState.errors.companyName = undefined;
												})
											}
											error={registerState.errors.companyName}
										/>
									)}
									<Flex className="login-flow__row">
										<TextInput
											className="flex-1"
											label={intl.formatMessage(
												isCompanyRegistration
													? WLIDS.form.contactFirstName
													: WLIDS.form.firstName,
											)}
											placeholder={intl.formatMessage(
												isCompanyRegistration
													? WLIDS.form.contactFirstNamePlaceholder
													: WLIDS.form.firstNamePlaceholder,
											)}
											required
											value={registerState.newUser.firstName ?? ''}
											onChange={e =>
												runInAction(() => {
													registerState.newUser.firstName = e.target.value;
													registerState.errors.firstName = undefined;
												})
											}
											error={registerState.errors.firstName}
										/>
										<TextInput
											className="flex-1"
											label={intl.formatMessage(
												isCompanyRegistration
													? WLIDS.form.contactLastName
													: WLIDS.form.lastName,
											)}
											placeholder={intl.formatMessage(
												isCompanyRegistration
													? WLIDS.form.contactLastNamePlaceholder
													: WLIDS.form.lastNamePlaceholder,
											)}
											required
											value={registerState.newUser.lastName ?? ''}
											onChange={e =>
												runInAction(() => {
													registerState.newUser.lastName = e.target.value;
													registerState.errors.lastName = undefined;
												})
											}
											error={registerState.errors.lastName}
										/>
									</Flex>
									<TextInput
										label={intl.formatMessage(WLIDS.form.email)}
										placeholder={intl.formatMessage(WLIDS.form.emailPlaceholder)}
										required
										type="email"
										autoComplete="username"
										onInvalid={onEmailInvalid}
										onInput={(e: React.FormEvent<HTMLInputElement>) =>
											e.currentTarget.setCustomValidity('')
										}
										value={registerState.newUser.email ?? ''}
										onChange={e =>
											runInAction(() => {
												registerState.newUser.email = e.target.value;
												registerState.errors.email = undefined;
											})
										}
										error={registerState.errors.email}
									/>
									{!passwordlessRegistration && (
										<PasswordInput
											label={intl.formatMessage(WLIDS.form.password)}
											placeholder={intl.formatMessage(WLIDS.form.password)}
											required
											autoComplete="current-password"
											data-test-id="register-password-input"
											value={registerState.newUser.password ?? ''}
											onChange={e =>
												runInAction(() => {
													registerState.newUser.password = e.target.value;
													registerState.errors.password = undefined;
												})
											}
											error={registerState.errors.password}
										/>
									)}
									{(isTeamMember || appConfigStore.userPhoneEnabled) && (
										<PhoneInputField
											model={registerState.newUser}
											modelProperty="phone"
											label={
												<>
													<FormattedMessage {...WLIDS.form.mobileNumber} />
													{isTeamMember && ' (optional)'}
												</>
											}
											placeholder={intl.formatMessage(WLIDS.form.mobileNumberPlaceholder)}
											required={!isTeamMember && appConfigStore.userPhoneRequired}
											onAfterChange={() =>
												runInAction(() => {
													registerState.errors.phone = undefined;
												})
											}
											error={registerState.errors.phone}
										/>
									)}
									{(isTeamMember || appConfigStore.userPostcodeEnabled) && (
										<TextInput
											label={`${intl.formatMessage(WLIDS.form.postcode)}${isTeamMember ? ' (optional)' : ''}`}
											placeholder={intl.formatMessage(WLIDS.form.postcodePlaceholder)}
											required={!isTeamMember}
											value={registerState.newUser.postcode ?? ''}
											onChange={e =>
												runInAction(() => {
													registerState.newUser.postcode = e.target.value;
													registerState.errors.postcode = undefined;
												})
											}
											error={registerState.errors.postcode}
										/>
									)}
								</Flex>
								<Flex direction="col" gap="xs">
									<Flex gap="xs">
										{mode === 'default' && (
											<Button
												display={Display.Outline}
												colors={Colors.Secondary}
												onClick={() => dispatch(LoginFlowActionCreators.reset())}
												newStyles
												buttonProps={{ style: { flex: 2 } }}
											>
												<FormattedMessage {...WLIDS.actions.back} />
											</Button>
										)}
										<Button
											type="submit"
											display={Display.Solid}
											colors={Colors.Primary}
											newStyles
											buttonProps={{ style: { flex: 5 } }}
											disabled={loading.value}
										>
											<FormattedMessage {...WLIDS.auth.confirmAccount} />
											{loading.value && <LoaderInsideButton />}
										</Button>
									</Flex>
								</Flex>
							</>
						)}
						{state.mode === 'sentRegistration' && (
							<>
								<Text>
									{store.isStaff
										? (() => {
												const recipient = registerState.newUser.email;
												const roleLabel =
													selectedRole === 'User' ? 'a customer' : `a ${selectedRole}`;
												const roleHint = isTeamMember
													? `They'll use the link to set their password and sign in as ${roleLabel}.`
													: "They'll use the link to set their password and sign in.";
												return (
													<>
														{'An activation email has been sent to '}
														<b>{recipient}</b>
														{`. ${roleHint}`}
													</>
												);
											})()
										: registrationMessage || (
												<FormattedMessage
													id={WLIDS.auth.checkYourInboxDescription.id}
													defaultMessage={WLIDS.auth.checkYourInboxDescription.defaultMessage}
												/>
											)}
								</Text>
								{store.isStaff && mode === 'registrationOnly' && (
									<Button
										display={Display.Solid}
										colors={Colors.Primary}
										newStyles
										onClick={() => navigate('/users')}
									>
										Back to users
									</Button>
								)}
								{!store.isStaff && (mode === 'convertGuest' || mode === 'registrationOnly') && (
									<Button
										display={Display.Solid}
										colors={Colors.Primary}
										newStyles
										onClick={() => navigate('/home', { replace: true })}
									>
										<FormattedMessage {...WLIDS.actions.goHome} />
									</Button>
								)}
							</>
						)}
						{state.mode === 'forgotPassword' && (
							<>
								<Flex direction="col" gap="xs">
									{state.sent && (
										<Text>
											<FormattedMessage {...WLIDS.auth.passwordAfterReset} />
										</Text>
									)}
									{!state.sent && (
										<>
											<Text>
												<FormattedMessage {...WLIDS.auth.passwordReset} />
											</Text>
											<TextField
												model={localState}
												modelProperty="email"
												label={<FormattedMessage {...WLIDS.form.email} />}
												placeholder={intl.formatMessage(WLIDS.form.emailPlaceholder)}
												inputProps={{ autoComplete: 'username' }}
												isRequired
												onAfterChange={() => {
													runInAction(() => {
														localState.errors.email = undefined;
													});
												}}
												errors={localState.errors.email}
											/>
										</>
									)}
								</Flex>
								{!state.sent && (
									<Flex gap="xs">
										<Button
											display={Display.Outline}
											colors={Colors.Secondary}
											newStyles
											onClick={() => dispatch(LoginFlowActionCreators.reset())}
											buttonProps={{ style: { flex: 2 } }}
										>
											<FormattedMessage {...WLIDS.actions.back} />
										</Button>
										<Button
											type="submit"
											display={Display.Solid}
											colors={Colors.Primary}
											newStyles
											buttonProps={{ style: { flex: 5 } }}
										>
											<FormattedMessage {...WLIDS.auth.sendResetEmail} />
											{loading.value && <LoaderInsideButton />}
										</Button>
									</Flex>
								)}
								{state.sent && (
									<Button
										display={Display.Outline}
										colors={Colors.Secondary}
										newStyles
										onClick={() => dispatch(LoginFlowActionCreators.reset())}
									>
										<FormattedMessage {...WLIDS.actions.back} />
									</Button>
								)}
							</>
						)}
						{state.mode === 'twoFactorRequired' && (
							<>
								{state.setupRequired && (
									<>
										<Flex direction="col" gap="xs">
											<Text>
												<FormattedMessage {...WLIDS.auth.twoFactorScanQrCode} />
												<a href={GoogleAuthenticatorLink}>Google Authenticator</a>
											</Text>
											<StyledQrCode
												value={buildOtpUrl(
													whiteLabelStore.config.companyName,
													localState.email,
													state.setupRequired.code,
													whiteLabelStore.config.companyName,
												)}
											/>
											<button
												style={{
													height: 'unset',
													marginTop: '1rem',
													display: 'flex',
													alignItems: 'center',
													gap: '0.5rem',
													background: 'none',
													border: 'none',
												}}
												onClick={() => {
													if (state.setupRequired) {
														navigator?.clipboard
															?.writeText(state.setupRequired?.code)
															.then(() => {
																alertToast('Code copied!', 'success');
															});
													}
												}}
											>
												{formatCode(state.setupRequired.code)}
												<img className="copy-icon" src={CopySvg} alt="copy-icon" />
											</button>
										</Flex>
										<Button
											type="submit"
											display={Display.Solid}
											colors={Colors.Primary}
											newStyles
											onClick={() => dispatch(LoginFlowActionCreators.twoFactorRequired())}
										>
											<FormattedMessage {...WLIDS.actions.continue} />
											{loading.value && <LoaderInsideButton />}
										</Button>
									</>
								)}
								{!state.setupRequired && (
									<>
										<Text>
											<FormattedMessage {...WLIDS.auth.twoFactorEnterCode} />
										</Text>
										<Flex direction="col" gap="md">
											<Flex direction="col" align="center">
												<PinCodeInput
													className="input-section"
													length={PinCodeLength}
													onComplete={pinCode => {
														runInAction(() => {
															twoFactorState.pinCode = pinCode;
														});
													}}
													resetDependencies={[twoFactorState.attemptsLeft]}
												/>
												{twoFactorState.attemptsLeft !== undefined && (
													<Errors
														errors={intl.formatMessage(WLIDS.auth.invalidCodeAttempts, {
															attempts: twoFactorState.attemptsLeft,
														})}
													/>
												)}
												<Checkbox
													model={twoFactorState}
													modelProperty="rememberMe"
													className="authenticator__remember-me-checkbox"
													label={intl.formatMessage(WLIDS.auth.skipTwoFactor)}
													onChecked={(_, checked) => {
														runInAction(() => {
															twoFactorState.rememberMe = checked;
														});
													}}
												/>
											</Flex>
											<Button
												type="submit"
												display={Display.Solid}
												colors={Colors.Primary}
												newStyles
												disabled={twoFactorState.pinCode?.length !== PinCodeLength}
											>
												<FormattedMessage {...WLIDS.actions.submit} />
												{loading.value && <LoaderInsideButton />}
											</Button>
										</Flex>
									</>
								)}
							</>
						)}
						{state.mode === 'accountLocked' && (
							<>
								<Text>
									{/* The wait comes from the server so it matches what it enforces; the old fixed
									    text is only for a response that did not say. */}
									{state.lockoutMinutes === undefined && (
										<FormattedMessage {...WLIDS.auth.twoFactorLocked} />
									)}
									{state.lockoutMinutes === 0 && (
										<FormattedMessage {...WLIDS.auth.accountDeactivated} />
									)}
									{!!state.lockoutMinutes && (
										<FormattedMessage
											{...WLIDS.auth.lockedTryAgainIn}
											values={{ minutes: state.lockoutMinutes }}
										/>
									)}
								</Text>
								<LinkAsButton to="/" replace colors={Colors.Primary} display={Display.Solid}>
									<FormattedMessage {...WLIDS.actions.goHome} />
								</LinkAsButton>
							</>
						)}
						{state.mode === 'emailCode' && (
							<Flex direction="col" gap="md">
								<Text>
									<FormattedMessage
										{...WLIDS.auth.emailCodeSent}
										values={{
											email:
												state.purpose === 'guestLogin'
													? guestState.user.email
													: registerState.newUser.email,
										}}
									/>
								</Text>
								<Flex direction="col" align="center" data-test-id="wizard-email-code-input">
									<PinCodeInput
										className="input-section"
										length={PinCodeLength}
										onComplete={pinCode => {
											runInAction(() => {
												emailCodeState.pinCode = pinCode;
											});
										}}
										resetDependencies={[emailCodeState.attemptsLeft]}
									/>
									{emailCodeState.attemptsLeft !== undefined && (
										<Errors
											errors={intl.formatMessage(WLIDS.auth.invalidCodeAttempts, {
												attempts: emailCodeState.attemptsLeft,
											})}
										/>
									)}
								</Flex>
								<Button
									type="submit"
									display={Display.Solid}
									colors={Colors.Primary}
									newStyles
									disabled={emailCodeState.pinCode?.length !== PinCodeLength}
									buttonProps={{ 'data-test-id': 'wizard-email-code-submit' } as any}
								>
									<FormattedMessage {...WLIDS.actions.submit} />
									{loading.value && <LoaderInsideButton />}
								</Button>
								<Button
									display={Display.Text}
									buttonProps={{ 'data-test-id': 'wizard-email-code-resend' } as any}
									onClick={() => {
										if (loading.value) {
											return;
										}
										if (state.purpose === 'guestLogin') {
											onContinueAsGuest(undefined, true);
										} else {
											onConvertGuest(undefined, true);
										}
									}}
								>
									<FormattedMessage {...WLIDS.auth.resendEmailCode} />
								</Button>
							</Flex>
						)}
						{state.mode === 'guestSetup' && (
							<>
								<Flex direction="col" gap="xs">
									<Flex className="login-flow__row">
										<TextInput
											className="flex-1"
											label={intl.formatMessage(WLIDS.form.firstName)}
											placeholder={intl.formatMessage(WLIDS.form.firstNamePlaceholder)}
											data-test-id="wizard-guest-first-name"
											required
											value={guestState.user.firstName ?? ''}
											onChange={e =>
												runInAction(() => {
													guestState.user.firstName = e.target.value;
													guestState.errors.firstName = undefined;
												})
											}
											error={guestState.errors.firstName}
										/>
										<TextInput
											className="flex-1"
											label={intl.formatMessage(WLIDS.form.lastName)}
											placeholder={intl.formatMessage(WLIDS.form.lastNamePlaceholder)}
											data-test-id="wizard-guest-last-name"
											required
											value={guestState.user.lastName ?? ''}
											onChange={e =>
												runInAction(() => {
													guestState.user.lastName = e.target.value;
													guestState.errors.lastName = undefined;
												})
											}
											error={guestState.errors.lastName}
										/>
									</Flex>
									<TextInput
										label={intl.formatMessage(WLIDS.form.email)}
										placeholder={intl.formatMessage(WLIDS.form.emailPlaceholder)}
										required
										type="email"
										autoComplete="username"
										data-test-id="wizard-guest-email"
										value={guestState.user.email ?? ''}
										onChange={e =>
											runInAction(() => {
												guestState.user.email = e.target.value;
												guestState.errors.email = undefined;
											})
										}
										error={guestState.errors.email}
									/>
									{appConfigStore.userPhoneEnabled && (
										<PhoneInputField
											model={guestState.user}
											modelProperty="phone"
											label={<FormattedMessage {...WLIDS.form.mobileNumber} />}
											placeholder={intl.formatMessage(WLIDS.form.mobileNumberPlaceholder)}
											required={appConfigStore.userPhoneRequired}
											data-test-id="wizard-guest-phone"
											onAfterChange={() =>
												runInAction(() => {
													guestState.errors.phone = undefined;
												})
											}
											error={guestState.errors.phone}
										/>
									)}
									{appConfigStore.userPostcodeEnabled && (
										<TextInput
											label={intl.formatMessage(WLIDS.form.postcode)}
											placeholder={intl.formatMessage(WLIDS.form.postcodePlaceholder)}
											data-test-id="wizard-guest-postcode"
											required
											value={guestState.user.postcode ?? ''}
											onChange={e =>
												runInAction(() => {
													guestState.user.postcode = e.target.value;
													guestState.errors.postcode = undefined;
												})
											}
											error={guestState.errors.postcode}
										/>
									)}
								</Flex>
								<Flex direction="col" gap="xs">
									<Flex gap="xs">
										<Button
											display={Display.Outline}
											colors={Colors.Secondary}
											onClick={() => dispatch(LoginFlowActionCreators.reset())}
											newStyles
											buttonProps={{ style: { flex: 2 } }}
										>
											<FormattedMessage {...WLIDS.actions.back} />
										</Button>
										<Button
											type="submit"
											display={Display.Solid}
											colors={Colors.Primary}
											newStyles
											buttonProps={{ style: { flex: 5 } }}
										>
											<FormattedMessage {...WLIDS.actions.continue} />
											{loading.value && <LoaderInsideButton />}
										</Button>
									</Flex>
								</Flex>
							</>
						)}
					</Flex>
				</Card>
			</form>
		</div>
	);
}

const buildOtpUrl = (application: string, userName: string, secret: string, issuer: string): string => {
	const escapedApp = encodeURIComponent(application);
	const escapedUser = encodeURIComponent(userName);
	const escapedIssuer = encodeURIComponent(issuer);
	return `otpauth://totp/${escapedApp}:${escapedUser}?secret=${secret}&issuer=${escapedIssuer}&digits=6`;
};
